import { useState, type ReactNode } from "react";
import type { ConditionsView, FieldType, InventoryResponse, ProfileField } from "@starsach/shared-types";
import { useClearOverride, useConfigVersions, useCreateProfile, useInventory, useRollback, useSetAlias, useSetOverride } from "../../api/hooks";
import { Card, DataTable, EmptyState, ErrorState, RelativeTime, btnPrimary, btnSecondary, inputClass } from "../../components/ui";
import { FIELD_TYPES, FIELD_TYPE_LABELS, fieldProblem, newField } from "../../lib/profile-fields";

/*
 * The pieces of Flow configuration that read the same on every screen of
 * it (requirements.md section 10): the four conditions, adding a field,
 * overrides, history, the alias fixer, and creating a profile.
 */
/**
 * Section 10.6 keeps four conditions apart, and so does this: a version
 * gap and a stale override are operational facts, while sync lag and
 * inventory drift mean something is broken. Conflating them trains
 * people to ignore the two that matter.
 */
export function Conditions({ conditions, environmentId }: { conditions: ConditionsView; environmentId: string }) {
  const drift = conditions.inventoryDrift ?? [];
  const faults = [
    conditions.syncLag && {
      key: "sync",
      text: (
        <>
          This environment is serving {conditions.syncLag.syncedVersion === undefined ? "no version" : `v${conditions.syncLag.syncedVersion}`} but v{conditions.syncLag.publishedVersion} was
          published <RelativeTime iso={conditions.syncLag.publishedAt} />.{" "}
          {conditions.syncLag.syncedAt ? (
            <>It last pulled its configuration <RelativeTime iso={conditions.syncLag.syncedAt} />.</>
          ) : (
            "It has never pulled its configuration - check that the flow configuration component is deployed there."
          )}
        </>
      ),
    },
    drift.length > 0 && {
      key: "drift",
      text: `${drift.length} reference no longer resolves in this instance. Nothing authored changed; the environment moved underneath it.`,
    },
  ].filter(Boolean) as Array<{ key: string; text: ReactNode }>;

  const facts = [
    conditions.unbound && {
      key: "unbound",
      text: "No number reaches this profile in this environment yet, so nothing is served from it. Bind one under Numbers below.",
    },
    conditions.pendingSync && {
      key: "pending",
      text: (
        <>
          v{conditions.pendingSync.publishedVersion} was published <RelativeTime iso={conditions.pendingSync.publishedAt} /> and this environment has not pulled it yet; it checks every few minutes.
        </>
      ),
    },
    conditions.versionGap && {
      key: "gap",
      text: `${conditions.versionGap.behind} version${conditions.versionGap.behind === 1 ? "" : "s"} behind ${conditions.versionGap.sourceEnvironmentId}.`,
    },
    ...conditions.staleOverrides.map((o) => ({
      key: `override-${o.field}`,
      text: `Override on ${o.field} has been active ${o.ageDays} days${o.expiresAt ? " and has expired" : " with no expiry"}, set by ${o.setBy}.`,
    })),
  ].filter(Boolean) as Array<{ key: string; text: ReactNode }>;

  if (faults.length === 0 && facts.length === 0) return null;
  return (
    <div className="space-y-2">
      {faults.length > 0 && (
        <div role="alert" className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-100">
          <p className="font-medium">Needs attention</p>
          <ul className="mt-1 list-disc pl-5">{faults.map((f) => <li key={f.key}>{f.text}</li>)}</ul>
          {drift.map((d) => <AliasFixer key={d.field} environmentId={environmentId} drift={d} />)}
        </div>
      )}
      {facts.length > 0 && (
        <div role="status" className="rounded border border-neutral-200 bg-neutral-50 p-3 text-sm dark:border-neutral-800 dark:bg-neutral-900">
          <ul className="list-disc pl-5">{facts.map((f) => <li key={f.key}>{f.text}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

/** Section 10.2: everything is a (type, name); the name is what the contact flow reads it by. */
export function AddField({ existing, inventory, onAdd, only }: { existing: ProfileField[]; inventory: InventoryResponse | undefined; onAdd: (field: ProfileField) => void; only?: FieldType[] }) {
  const types = only ?? FIELD_TYPES;
  const [type, setType] = useState<FieldType>(types[0] ?? "announcement");
  const [name, setName] = useState("");
  const [problem, setProblem] = useState<string>();

  const add = () => {
    const why = fieldProblem(type, name, existing, inventory);
    setProblem(why);
    if (why) return;
    onAdd(newField(type, name, inventory));
    setName("");
  };

  return (
    <form
      className="mt-4 flex flex-wrap items-end gap-3 rounded border border-dashed border-neutral-300 p-3 dark:border-neutral-700"
      aria-label="Add a field"
      onSubmit={(e) => {
        e.preventDefault();
        add();
      }}
    >
      {types.length > 1 && (
        <label className="text-xs">
          Type
          <select className={inputClass} value={type} onChange={(e) => setType(e.target.value as FieldType)}>
            {types.map((t) => (
              <option key={t} value={t}>{FIELD_TYPE_LABELS[t]}</option>
            ))}
          </select>
        </label>
      )}
      <label className="text-xs">
        Name
        <input
          className={inputClass}
          value={name}
          placeholder="welcome"
          aria-describedby={problem ? "add-field-problem" : undefined}
          aria-invalid={problem ? true : undefined}
          onChange={(e) => {
            setName(e.target.value);
            setProblem(undefined);
          }}
        />
      </label>
      <button type="submit" className={btnSecondary}>{types.length === 1 ? `Add ${FIELD_TYPE_LABELS[types[0]!].toLowerCase().replace(/ \(.*\)$/, "")}` : "Add field"}</button>
      {problem && <p id="add-field-problem" role="alert" className="basis-full text-xs text-red-700 dark:text-red-300">{problem}</p>}
    </form>
  );
}

export function OverrideRow({
  environmentId,
  profileId,
  field,
  override,
  describe,
}: {
  environmentId: string;
  profileId: string;
  field: string;
  override: { value: unknown; expiresAt?: string; setBy: string; reason: string };
  /** How the override's value reads; an hours override is a closure, not a string to quote. */
  describe?: (value: unknown) => string | undefined;
}) {
  const clear = useClearOverride(environmentId);
  const described = describe?.(override.value);
  return (
    <p className="mt-1 rounded bg-amber-50 p-2 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-100">
      {described ?? <>Playing <q>{String(override.value)}</q> instead</>} - {override.reason}, set by {override.setBy}
      {override.expiresAt ? <> until <RelativeTime iso={override.expiresAt} /></> : ", with no expiry"}.
      <button type="button" className="ml-2 underline" disabled={clear.isPending} onClick={() => clear.mutate({ profileId, field })}>
        End override
      </button>
    </p>
  );
}

/**
 * Setting one is fast and low-ceremony, and never gated: it happens
 * during an incident, by someone who is not a developer. The form makes
 * the distinction unavoidable - until further notice, or until when?
 */
export function NewOverride({ environmentId, profileId, fields }: { environmentId: string; profileId: string; fields: ProfileField[] }) {
  const set = useSetOverride(environmentId);
  const [open, setOpen] = useState(false);
  const [field, setField] = useState(`${fields[0]?.type}/${fields[0]?.name}`);
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const [expiresAt, setExpiresAt] = useState("");

  if (!open) {
    return <button type="button" className="rounded border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-700" onClick={() => setOpen(true)}>Set an override</button>;
  }
  return (
    <form
      className="w-full space-y-2 rounded border border-neutral-200 p-3 dark:border-neutral-800"
      onSubmit={(e) => {
        e.preventDefault();
        set.mutate({ profileId, field, value, reason, ...(expiresAt ? { expiresAt: new Date(expiresAt).toISOString() } : {}) });
        setOpen(false);
      }}
    >
      <p className="text-sm font-medium">Override a field temporarily</p>
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="text-sm">Field
          <select className={inputClass} value={field} onChange={(e) => setField(e.target.value)}>
            {fields.map((f) => <option key={`${f.type}/${f.name}`} value={`${f.type}/${f.name}`}>{f.name}</option>)}
          </select>
        </label>
        <label className="text-sm">Play this instead
          <input className={inputClass} value={value} onChange={(e) => setValue(e.target.value)} required />
        </label>
        <label className="text-sm">Why
          <input className={inputClass} value={reason} onChange={(e) => setReason(e.target.value)} required />
        </label>
        <label className="text-sm">Until
          <input type="datetime-local" className={inputClass} value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} aria-describedby="override-expiry-hint" />
          <span id="override-expiry-hint" className="mt-1 block text-xs text-neutral-500">
            Leave empty for until further notice. An override with no expiry is shown as ageing until someone ends it.
          </span>
        </label>
      </div>
      <div className="flex gap-2">
        <button type="submit" className={btnPrimary}>Apply now</button>
        <button type="button" className={btnSecondary} onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </form>
  );
}

export function Versions({ environmentId, profileId }: { environmentId: string; profileId: string }) {
  const versions = useConfigVersions(environmentId, profileId);
  const rollback = useRollback(environmentId, profileId);
  if (!versions.data) return null;

  return (
    <Card title="History">
      <DataTable
        caption="Every published version of this profile in this environment, newest first"
        rows={versions.data.versions}
        rowKey={(v) => String(v.configVersion)}
        empty={<EmptyState title="Nothing published here yet" />}
        columns={[
          { key: "v", header: "Version", render: (v) => `v${v.configVersion}` },
          { key: "when", header: "Published", render: (v) => <RelativeTime iso={v.publishedAt} /> },
          { key: "who", header: "By", render: (v) => v.publishedBy },
          {
            key: "how", header: "Source",
            render: (v) => v.source === "promotion" ? `promoted from ${v.sourceEnvironmentId}` : v.source === "rollback" ? `rolled back to v${v.rolledBackFromVersion}` : "edited here",
          },
          {
            key: "action", header: "",
            render: (v) => (
              <button type="button" className="text-sm underline disabled:opacity-50" disabled={rollback.isPending} onClick={() => rollback.mutate({ version: v.configVersion })}>
                Roll back to this
              </button>
            ),
          },
        ]}
      />
      {rollback.isSuccess && <p role="status" className="mt-2 text-sm text-green-800 dark:text-green-300">Rolled back.</p>}
    </Card>
  );
}

/**
 * Section 10.2: a name that exists in every environment resolves on its
 * own, so this is only ever the exception - where the instance calls the
 * same thing something else, or where it was renamed underneath the
 * configuration. Offered exactly where the problem is reported.
 */
export function AliasFixer({ environmentId, drift }: { environmentId: string; drift: { field: string; type: string; ref: string } }) {
  const inventory = useInventory(environmentId);
  const setAlias = useSetAlias(environmentId);
  const [resolvesTo, setResolvesTo] = useState("");
  const candidates = (inventory.data?.items ?? []).filter((i) => i.type === drift.type);

  return (
    <form
      className="mt-2 flex flex-wrap items-end gap-2 border-t border-red-200 pt-2 dark:border-red-900"
      onSubmit={(e) => {
        e.preventDefault();
        if (resolvesTo) setAlias.mutate({ type: drift.type, name: drift.ref, resolvesTo });
      }}
    >
      <span className="text-sm">
        <code className="text-xs">{drift.ref}</code> ({drift.type}) is not in this instance.
      </span>
      <label className="text-xs">
        It is called
        <select
          className="mt-1 block rounded border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
          aria-label={`What ${drift.ref} is called here`}
          value={resolvesTo}
          onChange={(e) => setResolvesTo(e.target.value)}
        >
          <option value="">Choose a name</option>
          {candidates.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
        </select>
      </label>
      <button type="submit" className={btnPrimary} disabled={!resolvesTo || setAlias.isPending}>
        Map it here
      </button>
    </form>
  );
}



/** Name it; everything else is authored on the profile afterwards. The API records who created it. */
export function NewProfileForm({ environmentId, onCreated, onCancel }: { environmentId: string; onCreated: (profileId: string) => void; onCancel?: () => void }) {
  const create = useCreateProfile(environmentId);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  return (
    <Card title="New profile">
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          create.mutate({ name: name.trim(), ...(description.trim() ? { description: description.trim() } : {}) }, { onSuccess: (r) => onCreated(r.profileId) });
        }}
      >
        <label className="text-sm">
          <span className="block text-xs text-neutral-500">Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} required maxLength={128} placeholder="Global Helpdesk" className={`${inputClass} w-64`} />
        </label>
        <label className="text-sm">
          <span className="block text-xs text-neutral-500">Description (optional)</span>
          <input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={512} placeholder="One flow, every inbound number." className={`${inputClass} w-96`} />
        </label>
        <button type="submit" disabled={create.isPending || !name.trim()} className={btnPrimary}>
          {create.isPending ? "Creating…" : "Create profile"}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className={btnSecondary}>
            Cancel
          </button>
        )}
        {create.isError && <ErrorState error={create.error} />}
      </form>
    </Card>
  );
}
